import { Typography } from '@material-ui/core';
import { IElementsAGN, IElementsAGO } from 'api/Assessor/quorum';
import { ThreeDots } from 'components/Icons';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import AlterarRepresentanteModal, {
  IAlterarRepresentanteModalGenericProps,
} from 'components/Modal/__LEGACY__/AlterarRepresentanteModal/AlterarRepresentanteModal';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { formatCPFOrCNPJ } from 'helpers/utils/cpfCnpjMask';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import moment from 'moment';
import {
  Col,
  DropdownItem,
  DropdownMenu,
  IconButton,
  Paginator,
  Row,
  Select,
  Table,
} from 'plataforma-fundacao-componentes';
import { Fragment, useCallback, useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  assessorActionListarQuorumPorAgn,
  assessorActionListarQuorumPorAgo,
} from 'store/modules/assessor/actions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';
import { tabelaInitialState, tabelaReducer } from './reducer';
import useStyle from './style';

interface Props {
  idEvento: number;
  agencia: string[];
  nucleo: string[];
  quorumAtingidoOrRepresentados: string;
  tipoEvento: AssembleiaTipo;
  flgTodos: string;
  needReload?: boolean;
  onReload?: () => void;
  disableQuorum: boolean;
  reloadOnSend?: () => void;
}

export default function Tabela({
  idEvento,
  tipoEvento,
  flgTodos,
  agencia,
  nucleo,
  quorumAtingidoOrRepresentados,
  needReload,
  disableQuorum,
  reloadOnSend,
}: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { quorumIsLoading } = useSelector((state: IReducer) => state.assessor);
  const { usuario } = useSelector((state: IReducer) => state.auth);

  const [totalPages, setTotalPages] = useState<number>();
  const [{ count, page, size, AGNData, AGOData }, action] = useReducer(
    tabelaReducer,
    tabelaInitialState
  );
  const [dropOpened, setDropOpened] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState<boolean | undefined>();
  const [agn, setAgn] = useState<string>();
  const [nuc, setNuc] = useState<string>();
  const dispatchAction = useDispatch();
  const currentEvent = useSelector(
    (state: IReducer) => state.assembleiasAssessor.evento
  );

  const getFlag = (flag: string) => {
    if (flag === 'Todos') return null;
    if (flag === 'Sim') return 1;
    if (flag === 'Não') return 0;
    return 1;
  };

  const getAGNData = useCallback(async () => {
    const payload = {
      agencias: agencia,
      idEvento,
      nucleos: nucleo,
      page,
      size,
      quorumAtingido: getFlag(flgTodos),
    };
    dispatch(
      assessorActionListarQuorumPorAgn(
        payload,
        (data) => {
          action({ type: 'setAGNData', value: data.elements });
          action({ type: 'setCount', value: data.totalElements });
          setTotalPages(data.totalPages);
        },
        ErrorHandler
      )
    );
  }, [dispatch, agencia, idEvento, nucleo, page, size, flgTodos]);

  const getAGOData = useCallback(async () => {
    const payload = {
      agencias: agencia,
      idEvento,
      nucleos: nucleo,
      page,
      size,
      representado: getFlag(flgTodos),
    };
    dispatch(
      assessorActionListarQuorumPorAgo(
        payload,
        (data) => {
          action({ type: 'setAGOData', value: data.elements });
          action({ type: 'setCount', value: data.totalElements });
          setTotalPages(data.totalPages);
        },
        ErrorHandler
      )
    );
  }, [dispatch, agencia, idEvento, nucleo, page, size, flgTodos]);

  useEffect(() => {
    if (!needReload) return;

    if (
      ['AGO', 'AGE', 'AOE'].includes(tipoEvento) &&
      quorumAtingidoOrRepresentados !== 'Quórum atingido'
    ) {
      getAGOData();
    }
    if (
      ['REU', 'AGN'].includes(tipoEvento) &&
      quorumAtingidoOrRepresentados === 'Quórum atingido'
    ) {
      getAGNData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needReload,
    tipoEvento,
    quorumAtingidoOrRepresentados,
    agencia,
    idEvento,
    nucleo,
    page,
    size,
    flgTodos,
  ]);

  useEffect(() => {
    action({
      type: 'setPage',
      value: 1,
    });
  }, [count]);

  const toggleDropOpened = (reference: string, value: boolean) => {
    if (value) {
      setDropOpened(reference);
    } else if (dropOpened === reference) {
      setDropOpened(undefined);
    }
  };

  useEffect(() => {
    const filter = AGOData.filter(
      (t: IElementsAGO) => t.codigoAgencia === agn && t.numeroNucleo === nuc
    )[0];

    setFilter(filter?.flagModificacao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agn, nuc, dispatchAction, dispatch, needReload, currentEvent]);

  const modalAlterarRepresentante = useCallback(() => {
    const modalKey: string = getUniqueKey();
    dispatchAction(
      modalsActionsOpen(
        AlterarRepresentanteModal,
        {
          genericProps: {
            key: modalKey,
            idEvento: currentEvent!?.id,
            agencia: agn,
            nucleo: nuc,
            onSend: reloadOnSend,
            flgModificado: filter,
          } as IAlterarRepresentanteModalGenericProps,
        },
        modalKey
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchAction, dropOpened]);

  return (
    <Row>
      {quorumIsLoading && <Loading />}
      {!quorumIsLoading && !AGNData.length && !AGOData.length ? (
        <div className={classes.errorMessage}>
          <Typography component='p'>
            {translation.QuorumAssessorRoute.noData}
          </Typography>
        </div>
      ) : (
        <Fragment>
          <Col className={classes.root}>
            <Table
              columns={
                quorumAtingidoOrRepresentados === 'Representados'
                  ? [
                      {
                        key: 'codigoAgencia',
                        value: translation.QuorumAssessorRoute.agencia,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'numeroNucleo',
                        value: translation.QuorumAssessorRoute.nucleo,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'totalConvocados',
                        value: translation.QuorumAssessorRoute.qtdConvocados,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'totalPresentes',
                        value: translation.QuorumAssessorRoute.qtdPresentes,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'nomeCargo',
                        value: translation.QuorumAssessorRoute.coordenador,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'nomeConvocado',
                        value: translation.QuorumAssessorRoute.nomeConvocado,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'cpfCnpj',
                        value: translation.QuorumAssessorRoute.cpfCnpj,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'dataHora',
                        value: translation.QuorumAssessorRoute.dataHora,
                        props: { style: { textAlign: 'left' } },
                      },
                      { key: 'actions', value: '' },
                    ]
                  : [
                      {
                        key: 'codigoAgencia',
                        value: translation.QuorumAssessorRoute.agencia,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'numeroNucleo',
                        value: translation.QuorumAssessorRoute.nucleo,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'totalConvocados',
                        value: translation.QuorumAssessorRoute.qtdConvocados,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'totalPresentes',
                        value:
                          translation.QuorumAssessorRoute
                            .qtdContasRepresentadas,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'totalPresentesAptos',
                        value: translation.QuorumAssessorRoute.qtdContasAptas,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'percentualPresentes',
                        value: translation.QuorumAssessorRoute.presentes,
                        props: { style: { textAlign: 'left' } },
                      },
                      {
                        key: 'disableQuorum',
                        value: translation.VisualizacoesAssessor.quorumAtingido,
                        props: { style: { textAlign: 'left' } },
                      },
                    ]
              }
              lines={
                quorumAtingidoOrRepresentados === 'Representados'
                  ? AGOData &&
                    AGOData!?.map((row: IElementsAGO) => {
                      return {
                        codigoAgencia: row.codigoAgencia
                          ? row.codigoAgencia
                          : '0',
                        numeroNucleo: row.numeroNucleo ? row.numeroNucleo : '0',
                        totalConvocados: row.totalConvocados
                          ? row.totalConvocados
                          : '0',
                        totalPresentes: row.totalPresentes
                          ? row.totalPresentes
                          : '0',
                        nomeCargo: row.nomeCargo ? row.nomeCargo : '-',
                        nomeConvocado: row.nomeConvocado
                          ? row.nomeConvocado
                          : '-',
                        cpfCnpj: row.cpfCnpj
                          ? formatCPFOrCNPJ(row.cpfCnpj)
                          : '-',
                        dataHora: row.dataHora
                          ? moment(row.dataHora).format('DD/MM/YYYY - HH:mm')
                          : '-',
                        actions:
                          (currentEvent?.status !== AssembleiaStatus.Live &&
                            currentEvent?.existeQuorum === false) ||
                          (currentEvent?.status === AssembleiaStatus.Live &&
                            currentEvent?.existeQuorum === false) ||
                          (currentEvent?.status ===
                            AssembleiaStatus.Encerrado &&
                            currentEvent?.existeQuorum) ? undefined : (
                            <div>
                              <DropdownMenu
                                opened={
                                  String(dropOpened) ===
                                  String(
                                    `${row.codigoAgencia}${row.numeroNucleo}`
                                  )
                                }
                                setOpened={(value) => {
                                  toggleDropOpened(
                                    `${row.codigoAgencia}${row.numeroNucleo}`,
                                    value
                                  );
                                }}
                                content={
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <DropdownItem
                                      disabled={
                                        !usuario?.perfil.assessorRoute.quorum
                                          .alterarRepresentante || filter
                                      }
                                      label={
                                        filter
                                          ? translation.QuorumAssessorRoute
                                              .representanteAlterado
                                          : translation.QuorumAssessorRoute
                                              .alterarRepresentante
                                      }
                                      onClick={() => {
                                        setDropOpened(undefined);
                                        modalAlterarRepresentante();
                                      }}
                                    />
                                  </div>
                                }
                              >
                                <IconButton
                                  icon={<ThreeDots />}
                                  disabled={
                                    !usuario?.perfil.assessorRoute.quorum
                                      .alterarRepresentante ||
                                    row.totalPresentes <= 1
                                  }
                                  onClick={() => {
                                    toggleDropOpened(
                                      `${row.codigoAgencia}${row.numeroNucleo}`,
                                      dropOpened !==
                                        `${row.codigoAgencia}${row.numeroNucleo}`
                                    );
                                    setAgn(row.codigoAgencia);
                                    setNuc(row.numeroNucleo);
                                  }}
                                />
                              </DropdownMenu>
                            </div>
                          ),
                      };
                    })
                  : AGNData &&
                    AGNData!?.map((row: IElementsAGN) => {
                      return {
                        codigoAgencia: row.codigoAgencia
                          ? row.codigoAgencia
                          : '0',
                        numeroNucleo: row.numeroNucleo ? row.numeroNucleo : '0',
                        totalConvocados: row.totalConvocados
                          ? row.totalConvocados
                          : '0',
                        totalPresentes: row.totalPresentes
                          ? row.totalPresentes
                          : '0',
                        totalPresentesAptos: row.totalPresentesAptos
                          ? row.totalPresentesAptos
                          : '0',
                        percentualPresentes: row.percentualPresentes
                          ? `${Number(row.percentualPresentes.toFixed(2))}%`
                          : '0%',
                        disableQuorum: disableQuorum
                          ? '-'
                          : !row.quorumAtingido
                          ? 'Não'
                          : 'Sim',
                      };
                    })
              }
            />
          </Col>
          <Col cols={[12, 12, 6, 6, 6]}>
            <Col cols={[12, 12, 6, 6, 4]} noPadding>
              <Select
                data-testid={'row-per-page'}
                label={'Linhas por página'}
                options={[
                  {
                    label: '10',
                    value: 10,
                  },
                  {
                    label: '25',
                    value: 25,
                  },
                  {
                    label: '50',
                    value: 50,
                  },
                  {
                    label: '100',
                    value: 100,
                  },
                ]}
                onChange={(e) => {
                  action({
                    type: 'setSize',
                    value: e,
                  });
                  action({
                    type: 'setPage',
                    value: 1,
                  });
                }}
                placeholder={'teste'}
                value={size}
                disabled={false}
                showPanelFooter={false}
                clearButton={false}
              />
            </Col>
          </Col>
          <Col cols={[12, 12, 6, 6, 6]} end>
            <Paginator
              paginaAtual={page}
              quantidadeDePaginas={totalPages!}
              maxLength={7}
              onChange={(e: number) =>
                action({
                  type: 'setPage',
                  value: e,
                })
              }
            />
          </Col>
        </Fragment>
      )}
    </Row>
  );
}
